import { useRef, useState, useMemo, useEffect } from 'react';
import Webcam from 'react-webcam';
import { useGeolocated } from 'react-geolocated';
import { Box, Stack, Paper, Chip, Button, TextField, CircularProgress, Typography, IconButton } from '@mui/material';
import { CameraAlt, AddAPhoto, GpsFixed, Upload, CheckCircle, ErrorOutline, FlashlightOn, FlashlightOff } from '@mui/icons-material';
import { MIMEURLToByte, postToDB, postNewCap, postTitle } from '../Common';
import { parseCartellone, votoColor, votoLabel } from '../lib/parse';
import RecorderInline from './RecorderInline';

// risoluzione: si chiede il massimo che la camera offre (ideal = soft constraint,
// il browser concede il meglio disponibile; i vecchi 1280×1024 buttavano via ~10x di pixel)
const VCONST = { width: { ideal: 4096 }, height: { ideal: 3072 }, facingMode: 'environment' };

const CSS_MAX_ZOOM = 5;   // limite dello zoom digitale (oltre, il ritaglio degrada troppo)

export default function ScanView({ notify, onDataChanged }) {
  const webcamref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [elemid, setElemid] = useState(null);
  const [title, setTitle] = useState('');
  const [nCntx, setNCntx] = useState(0);

  /* --- zoom fotocamera: nativo se la camera lo supporta, altrimenti digitale --- */
  const [zoom, setZoomState] = useState(1);
  const nativeZoomRef = useRef(null);            // { track, min, max }
  const trackRef = useRef(null);
  const [torch, setTorch] = useState(null);      // null = non disponibile, false/true = stato
  const [camRes, setCamRes] = useState('');
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);

  const applyZoom = (z) => {
    const nz = nativeZoomRef.current;
    const lim = nz ? Math.min(8, nz.max) : CSS_MAX_ZOOM;
    const clamped = Math.min(lim, Math.max(1, z));
    setZoomState(clamped);
    if (nz) nz.track.applyConstraints({ advanced: [{ zoom: Math.max(nz.min, clamped) }] }).catch(() => {});
  };

  const onUserMedia = (stream) => {
    try {
      const track = stream.getVideoTracks()[0];
      trackRef.current = track;
      const caps = track.getCapabilities?.() || {};
      if (caps.zoom && caps.zoom.max > (caps.zoom.min ?? 1)) {
        nativeZoomRef.current = { track, min: caps.zoom.min ?? 1, max: caps.zoom.max };
      }
      if (caps.torch) setTorch(false);
      const s = track.getSettings?.() || {};
      if (s.width) setCamRes(`${s.width}×${s.height}`);
    } catch { /* nessuno zoom nativo: si usa quello digitale */ }
  };

  const toggleTorch = () => {
    const track = trackRef.current;
    if (!track || torch == null) return;
    track.applyConstraints({ advanced: [{ torch: !torch }] })
      .then(() => setTorch(!torch))
      .catch(() => notify('Torcia non disponibile su questa camera', 'error'));
  };

  const camPointerDown = (e) => {
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { d: Math.hypot(a.x - b.x, a.y - b.y), z: zoom };
    }
  };
  const camPointerMove = (e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      applyZoom(pinchRef.current.z * (Math.hypot(a.x - b.x, a.y - b.y) / pinchRef.current.d));
    }
  };
  const camPointerUp = (e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
  };

  /** Scatta alla risoluzione piena del flusso video, rispettando lo zoom:
   *  nativo = frame già zoomato dall'hardware; digitale = ritaglio centrale. */
  const captureFrame = () => {
    const video = webcamref.current?.video;
    if (!video || !video.videoWidth) return null;
    const vw = video.videoWidth, vh = video.videoHeight;
    const digital = !nativeZoomRef.current && zoom > 1.01;
    const sw = digital ? vw / zoom : vw, sh = digital ? vh / zoom : vh;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw); canvas.height = Math.round(sh);
    canvas.getContext('2d').drawImage(video, (vw - sw) / 2, (vh - sh) / 2, sw, sh, 0, 0, canvas.width, canvas.height);
    let out = canvas.toDataURL('image/jpeg', 0.87);
    if (out.length > 8_000_000) out = canvas.toDataURL('image/jpeg', 0.75);   // tetto ~6MB reali per l'upload
    return out;
  };

  const { coords, isGeolocationAvailable, isGeolocationEnabled, getPosition } = useGeolocated({
    positionOptions: { enableHighAccuracy: true, maximumAge: 0, timeout: Infinity },
    userDecisionTimeout: 15000,
    watchPosition: true,
  });

  // coordsRef sempre aggiornato: il setTimeout di shotPrimary deve leggere il fix GPS fresco, non la closure
  const coordsRef = useRef(coords);
  useEffect(() => { coordsRef.current = coords; }, [coords]);

  const meta = useMemo(() => parseCartellone({ title, dataChange: null }), [title]);
  const vc = votoColor(meta.voto);

  const shotPrimary = () => {
    const img = captureFrame();
    if (!img) { notify('Fotocamera non pronta', 'error'); return; }
    getPosition();
    setBusy(true);
    setTimeout(() => {   // come nell'app originale: 4s per assestare il GPS
      const c = { id: null, latitude: coordsRef.current?.latitude, longitude: coordsRef.current?.longitude,
                  objimgsrc: MIMEURLToByte(img), creationdate: null };
      postToDB(c, setBusy, () => notify('Errore nel salvataggio', 'error'), (ret) => {
        setElemid(ret.id);
        setNCntx(0);
        notify(`Salvato ✓ Cartellone ${ret.id} creato`, 'success');
        onDataChanged();
      });
    }, 4000);
  };

  const shotSecondary = () => {
    const img = captureFrame();
    if (!img || elemid == null) return;
    setBusy(true);
    setTimeout(() => {
      postNewCap(MIMEURLToByte(img), elemid, setBusy, () => {
        setNCntx(n => n + 1);
        notify(`Foto contesto aggiunta al ${elemid} ✓`, 'success');
        onDataChanged();
      }, () => notify('Errore: foto contesto non salvata', 'error'));
    }, 1000);
  };

  const assegnaTitolo = () => {
    if (elemid == null || !title.trim()) return;
    postTitle({ id: elemid, title: title.trim() }, (r) => {
      notify(r === 'success' ? `Titolo assegnato al ${elemid} ✓` : 'Errore titolo', r === 'success' ? 'success' : 'error');
      onDataChanged();
    }, null);
  };

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Box onPointerDown={camPointerDown} onPointerMove={camPointerMove}
           onPointerUp={camPointerUp} onPointerCancel={camPointerUp}
           onDoubleClick={() => applyZoom(1)}
           sx={{ position: 'relative', flex: 1, minHeight: 200, bgcolor: '#0f172a', overflow: 'hidden',
                 touchAction: 'none' }}>
        {isGeolocationAvailable && isGeolocationEnabled && (
          <Webcam audio={false} ref={webcamref} videoConstraints={VCONST}
                  onUserMedia={onUserMedia}
                  style={{ width: '100%', height: '100%', objectFit: 'cover',
                           transform: nativeZoomRef.current ? 'none' : `scale(${zoom})`,
                           transformOrigin: 'center center' }} />
        )}
        <Stack direction="row" spacing={0.8}
               onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
               sx={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)' }}>
          {[1, 2, 4].map(z => (
            <Chip key={z} label={`${z}×`} onClick={() => applyZoom(z)}
                  sx={{ fontWeight: 800, fontSize: 14, px: 0.5, cursor: 'pointer',
                        bgcolor: Math.abs(zoom - z) < 0.05 ? '#1d4ed8' : 'rgba(15,23,42,.7)',
                        color: 'white', border: '1px solid rgba(255,255,255,.35)',
                        '&:hover': { bgcolor: '#1d4ed8' } }} />
          ))}
        </Stack>
        {torch != null && (
          <IconButton onClick={toggleTorch}
                      onPointerDown={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
                      sx={{ position: 'absolute', top: 6, right: 6, color: torch ? '#fbbf24' : 'white',
                            bgcolor: 'rgba(15,23,42,.7)', '&:hover': { bgcolor: 'rgba(15,23,42,.9)' } }}>
            {torch ? <FlashlightOn /> : <FlashlightOff />}
          </IconButton>
        )}
        <Chip size="small"
              label={`${zoom > 1.01 ? `${zoom.toFixed(1)}× — doppio tap reset` : 'pizzica per zoomare'}${camRes ? ` · ${camRes}` : ''}`}
              sx={{ position: 'absolute', bottom: 10, right: 10, bgcolor: 'rgba(15,23,42,.7)', color: 'white', fontSize: 11 }} />
        {(!isGeolocationAvailable || !isGeolocationEnabled) && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'white', p: 3, textAlign: 'center' }}>
            <Typography>Il GPS è necessario per censire i cartelloni — abilita la geolocalizzazione.</Typography>
          </Stack>
        )}
        <Chip icon={<GpsFixed sx={{ color: '#22c55e !important' }} />}
              label={coords ? `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}` : 'in attesa del GPS…'}
              sx={{ position: 'absolute', top: 10, left: 10, bgcolor: 'rgba(15,23,42,.8)', color: 'white', fontWeight: 600 }} />
        {busy && (
          <Stack alignItems="center" justifyContent="center"
                 sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(15,23,42,.55)' }}>
            <CircularProgress sx={{ color: 'white' }} />
            <Typography sx={{ color: 'white', mt: 1 }}>acquisizione…</Typography>
          </Stack>
        )}
      </Box>

      <Paper sx={{ borderRadius: '14px 14px 0 0', p: 2, pb: 2.5, mt: -1.5, zIndex: 2 }}>
        <Stack spacing={1.2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={meta.concessionaria || 'Concessionaria —'}
                  sx={{ color: meta.concessionaria ? '#1d4ed8' : '#94a3b8', fontWeight: 700 }} />
            <Chip size="small" label={`Voto ${votoLabel(meta.voto)}`}
                  sx={{ bgcolor: vc.bg, color: vc.fg, fontWeight: 800 }} />
            {elemid != null
              ? <Chip size="small" icon={<ErrorOutline />} label={`Da rivedere · id ${elemid}`} sx={{ bgcolor: '#fff7ed', color: '#c2410c', '& .MuiChip-icon': { color: 'inherit' } }} />
              : <Chip size="small" icon={<CheckCircle />} label="Pronto a scattare" sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', '& .MuiChip-icon': { color: 'inherit' } }} />}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth label="Titolo cartellone"
                       placeholder="es. DAMIR NO CIMASA FRONTE ELENKA V8"
                       value={title} onChange={(e) => setTitle(e.target.value)} />
            <Button variant="outlined" startIcon={<Upload />} disabled={elemid == null || !title.trim()}
                    onClick={assegnaTitolo} sx={{ whiteSpace: 'nowrap' }}>
              Assegna
            </Button>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="large" fullWidth startIcon={<CameraAlt />}
                    disabled={busy || !coords} onClick={shotPrimary}>
              Scatta principale
            </Button>
            <Button variant="outlined" size="large" fullWidth startIcon={<AddAPhoto />}
                    disabled={busy || elemid == null} onClick={shotSecondary}>
              Scatta contesto
            </Button>
          </Stack>

          <RecorderInline elem={elemid} notify={notify} />

          {elemid != null && (
            <Typography variant="caption" color="text.secondary">
              {nCntx} foto contesto aggiunte al cartellone {elemid} in questa sessione
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
